import style from "./PersonalData.module.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Controller, FieldError, useForm } from "react-hook-form";

import {
  Button,
  CheckBox,
  FieldGroup,
  FieldRow,
  FieldSet,
  Input,
  List,
  ModalDialog,
  Radio,
  Section,
  SelectBox,
} from "react-corner-ds";
import i18n from "../../../i18n";
import {
  CountriesResponse,
  CountryItem,
} from "../../../models/response/response.model";
import { PersonalDataInfo } from "../../../models/request/data/data.model";
import { definePatternByName } from "../../../utils/constants";
import { Request } from "../../../models/request/request.model";
import { useFlow } from "../../../hooks/useFlow/useFlow";
import { store } from "../../../redux/store";
import { EmailValidator } from "commons-validator-js";
import { Bank } from "../../../models/bank/bank.model";
import { Customer } from "../../../models/bank/customer/customer.model";
import GooglCaptchaDisclaimer from "../../googleCaptchaDisclaimer/googleCaptchaDisclaimer";
import {
  capitalizeFirst,
  parsePhoneNumber,
  phoneIsValid,
} from "../../../utils/reformat";

interface IPersonalData {
  modalRecap?: boolean;
  modalCallback?: () => void;
  mockObject?: Bank;
}

const PersonalData: React.FC<IPersonalData> = ({
  modalRecap,
  modalCallback,
  mockObject,
}) => {
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const { callSteploader } = useFlow();
  const [nextStepLoading, setNextStepLoading] = useState(false);
  const [country, setCountry] = useState("CH");

  const { t } = useTranslation();

  const { handleSubmit, formState, control, setValue, getValues } =
    useForm<PersonalDataInfo>({
      mode: "all",
      reValidateMode: "onChange",
      defaultValues: {
        firstName: "",
        lastName: "",
        email: "",
        gender: "001",
        phones: [
          { CountryCode: "+41", LocalNumber: "", Number: "", Type: "Mobile" },
        ],
        terms: false,
      },
    });

  useEffect(() => {
    let customerStore: Customer = mockObject?.Customer ?? {};

    if (store.getState().appConf.workflowEnabled) {
      customerStore = store.getState().pega.Data?.Bank.Customer ?? {};
    }

    if (customerStore) {
      setValue("firstName", customerStore.FirstName ?? "");
      setValue("lastName", customerStore.LastName ?? "");
      setValue("gender", customerStore.Salutation ?? "001");
      setValue("email", customerStore.Email ?? "");
      if (customerStore.Phones) {
        setValue(
          "phones.0.LocalNumber",
          customerStore.Phones[0].LocalNumber ?? ""
        );
        setValue(
          "phones.0.CountryCode",
          customerStore.Phones[0].CountryCode ?? "+41"
        );
        setValue("phones.0.Number", customerStore.Phones[0].Number ?? "");
        setCountry(
          countries.find(
            (item: CountryItem) =>
              "+" + item.phone_code === customerStore.Phones?.[0]?.CountryCode
          )?.alpha_2 ?? "CH"
        );
      }
    }
    setValue("terms", false);
  }, [countries, mockObject?.Customer, setValue]);

  const submitHandler = async (data: PersonalDataInfo) => {
    let body: Request = {
      Data: {
        FlowDirection: modalRecap ? "Still" : "Next",
        CampaignChannel: store.getState().queryParams.channelParam,
        Bank: {
          SatisfiesRequirementsDisclaimer: modalRecap ? true : data.terms,
          Customer: {
            Language: i18n.resolvedLanguage,
            Salutation: data.gender,
            Email: data.email.trim(),
            FirstName: capitalizeFirst(data.firstName.trim()),
            LastName: capitalizeFirst(data.lastName.trim()),
            Phones: [
              {
                CountryCode: data?.phones?.[0]?.CountryCode || "",
                LocalNumber: data?.phones[0].LocalNumber?.replaceAll(" ", ""),
                Number:
                  (data?.phones?.[0]?.CountryCode || "") +
                  data?.phones?.[0]?.LocalNumber?.replaceAll(" ", ""),
                Type: "Mobile",
              },
            ],
          },
        },
        CID: store.getState()?.queryParams.cid,
        Promotion: store.getState()?.queryParams.campaigntype,
      },
      Extra: {},
    };

    setNextStepLoading(true);
    if (modalRecap) {
      await callSteploader(body, false, "PersonalData");
      setNextStepLoading(false);
    } else {
      await callSteploader(body);
      setNextStepLoading(false);
    }

    modalCallback && modalRecap && modalCallback();
  };

  useEffect(() => {
    import(`../../../assets/countries/${i18n.resolvedLanguage}.json`).then(
      (resp: CountriesResponse) => {
        setCountries(resp.countries);
      }
    );
  }, [i18n.resolvedLanguage]);

  const validity = useCallback(
    (error: FieldError | undefined, isTouched: boolean) =>
      !error && isTouched ? true : undefined,
    []
  );

  const items = useMemo(() => {
    return countries.map((item: CountryItem) => ({
      label: [item.name, "+" + item.phone_code],
      value: "+" + item.phone_code,
      icon: item.alpha_2,
    }));
  }, [countries]);

  const hideModal = useCallback((ev: any) => {
    ev.preventDefault();
    setIsShowModal(false);
  }, []);

  const genderInput = useMemo(
    () => (
      <Controller
        control={control}
        name="gender"
        render={({ field: { onChange, name, value } }) => (
          <Radio
            name={name}
            value={value}
            options={[
              { text: t`screens.common.mr`, _value: "001" },
              { text: t`screens.common.mrs`, _value: "003" },
            ]}
            onChange={(updateValue: any) => {
              onChange(updateValue);
            }}
          />
        )}
      />
    ),
    [control, t]
  );

  const firstNameInput = useMemo(
    () => (
      <Controller
        control={control}
        name="firstName"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("FirstName"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            name={name}
            label={t`screens.personalData.firstName` || ""}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            valid={error ? false : validity(error, isTouched)}
            errorMsg={error?.message}
            maxLength={60}
            value={value}
          />
        )}
      />
    ),
    [control, t, validity]
  );

  const lastNameInput = useMemo(
    () => (
      <Controller
        control={control}
        name="lastName"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("LastName"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            name={name}
            label={t`screens.personalData.lastName` || ""}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onBlur={onBlur}
            valid={error ? false : validity(error, isTouched)}
            errorMsg={error?.message}
            maxLength={60}
            value={value}
          />
        )}
      />
    ),
    [control, t, validity]
  );

  const validator = useMemo(() => new EmailValidator(), []);

  const emailInput = useMemo(
    () => (
      <Controller
        control={control}
        name="email"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          validate: (value) => validator.isValid(value),
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            name={name}
            label={t`screens.personalData.email` || ""}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            type="email"
            onBlur={onBlur}
            valid={error ? false : validity(error, isTouched)}
            errorMsg={
              error?.message ||
              (!validator.isValid(value) &&
                t`screens.personalData.emailRequired`) ||
              ""
            }
            maxLength={50}
            value={value}
          />
        )}
      />
    ),
    [control, t, validator, validity]
  );

  const prefixSelectBox = useMemo(
    () => (
      <Controller
        control={control}
        name="phones.0.CountryCode"
        defaultValue={"+41"}
        render={({ field: { onChange, name, value } }) => (
          <SelectBox
            cssClass="prefix"
            value={value}
            previewIndex="1"
            items={items}
            name={name}
            onChange={(item: any) => {
              onChange(item.value);
              setCountry(item.icon);
              setValue("phones.0.LocalNumber", "");
            }}
          />
        )}
      />
    ),
    [control, items, setValue]
  );

  const phoneInput = useMemo(
    () => (
      <Controller
        control={control}
        name="phones.0.LocalNumber"
        defaultValue={""}
        rules={{
          validate: (value) => phoneIsValid(value, country),

          required: {
            value: true,
            message: t`screens.personalData.phoneRequired`,
          },
          minLength: {
            value: 7,
            message: t(`errors.input.minPhoneLength`, {
              min: "7",
            }),
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <Input
            type="tel"
            name={name}
            customCssClass={style.CustomPhoneInput}
            label={t`screens.personalData.phoneNr` || ""}
            placeholder="00 000 00 00"
            cssClass="prefixed"
            onBlur={onBlur}
            onChange={(ev: any) => {
              onChange(
                parsePhoneNumber(
                  getValues("phones.0.CountryCode"),
                  ev.target.value
                )
              );
            }}
            valid={error ? false : validity(error, isTouched)}
            errorMsg={error?.message}
            maxLength={13}
            value={value}
          >
            {prefixSelectBox}
          </Input>
        )}
      />
    ),
    [control, prefixSelectBox, t, validity, country, getValues]
  );

  const buildModal = useMemo(() => {
    return (
      <div>
        <h1>{t`screens.personalData.requirementsModal.title`}</h1>
        <p>{t`screens.personalData.requirementsModal.subTitle`}</p>
        <div>
          <List
            items={[
              {
                text: t`screens.personalData.requirementsModal.data1`,
                id: "1",
                state: "checked",
              },
              {
                text: t`screens.personalData.requirementsModal.data2`,
                id: "2",
                state: "checked",
              },
            ]}
          />
        </div>
        <div>
          <Button
            customCssClass={style.understoodButton}
            text={t`screens.personalData.requirementsModal.understood`}
            onClick={() => setIsShowModal(false)}
          />
        </div>
      </div>
    );
  }, [t]);

  const termsInput = useMemo(
    () => (
      <Controller
        control={control}
        name="terms"
        rules={{
          required: {
            value: modalRecap ? false : true,
            message: t`errors.input.required`,
          },
        }}
        render={({ field: { onChange, name } }) => (
          <FieldRow customCssClass={style.disclaimerRow}>
            <FieldGroup customCssClass={style.disclaimerCheckBox}>
              <CheckBox
                name={name}
                customCssClass={style.terms}
                onChange={(ev) => {
                  onChange(ev.target.checked);
                }}
              />
            </FieldGroup>
            <FieldGroup customCssClass={style.disclaimer}>
              <Trans
                i18nKey={t`screens.personalData.gtcAgree`}
                components={{
                  Requirements: (
                    <button
                      type="button"
                      className={style.RequirementsButton}
                      onClick={() => setIsShowModal(true)}
                    />
                  ),
                  Disclaimer: (
                    <a
                      target="_blank"
                      href={
                        store.getState().appConf.environment === "test" ||
                        store.getState().appConf.environment === "prep"
                          ? t`screens.common.disclaimerLink_prep`
                          : t`screens.common.disclaimerLink_prod`
                      }
                      rel="noreferrer"
                    >
                      {}
                    </a>
                  ),
                }}
              />
            </FieldGroup>
          </FieldRow>
        )}
      />
    ),
    [control, modalRecap, t]
  );

  return (
    <div className="container">
      <h1>{t`screens.personalData.pageTitle`}</h1>
      <h2>{t`screens.personalData.pageSubtitle`}</h2>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(submitHandler)}
        className={modalRecap ? style.modalView : ""}
      >
        <Section>
          <FieldSet>
            <FieldRow>
              <FieldGroup>{genderInput}</FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>{firstNameInput}</FieldGroup>
              <FieldGroup>{lastNameInput}</FieldGroup>
            </FieldRow>

            <div className={style.infoPanel}>
              <p>{t`screens.personalData.dataInfo`}</p>
            </div>

            <FieldRow>
              <FieldGroup>{emailInput}</FieldGroup>
            </FieldRow>

            <FieldRow>
              <FieldGroup>{phoneInput}</FieldGroup>
            </FieldRow>

            {!modalRecap ? (
              <FieldRow>
                <FieldGroup>{termsInput}</FieldGroup>
              </FieldRow>
            ) : (
              <></>
            )}
          </FieldSet>
        </Section>

        <Section>
          <FieldSet>
            <div className={style.btnHolder + " btnHolder"}>
              <Button
                text={t`screens.common.continue`}
                disabled={!formState.isValid || nextStepLoading}
                type="submit"
                icon={
                  nextStepLoading
                    ? { icon: "loader", position: "right", spin: true }
                    : undefined
                }
              />
              <GooglCaptchaDisclaimer></GooglCaptchaDisclaimer>
            </div>
          </FieldSet>
        </Section>
      </form>

      <ModalDialog
        id={"requirementsModal"}
        show={isShowModal}
        handleClose={hideModal}
        backdropClose={true}
        customCssClass={style.genericModal}
      >
        {buildModal}
      </ModalDialog>
    </div>
  );
};

export default PersonalData;
